import { Rect } from '../../drawing';
import { GregorioSyllable } from '../../gregorio-chant/structures/GregorioSyllable';
import { ChantContext } from '../../gregorio-chant/ChantContext';
import { ChantLayoutElement } from '../layout/ChantLayoutElement';
import { Lyric } from '../texts/Lyric';
import { ChantSegmentElement } from './ChantSegmentElement';
import { Clef } from '../clefs';
import { SyllableBreakpoint } from './SyllableBreakpoint';
import { min } from 'lodash';
import { HorizontalEpisema } from '../visualizers/HorizontalEpisema';
import { AboveLineText } from '../texts/AboveLineText';

export class ChantSyllableElement extends ChantLayoutElement {
  protected _lyric: Lyric | null = null;
  protected _aboveLineText: AboveLineText | null = null;
  protected _segments: ChantSegmentElement[] = [];
  protected _gregorioSyllable: GregorioSyllable;
  protected readonly _index?: number;

  get hasCustos() {
    return this._segments.some((e) => e.hasCustos);
  }

  get getCustosSegment() {
    return this._segments.find((e) => e.hasCustos);
  }

  get hasClef() {
    return this._segments.some((e) => e.hasClef);
  }

  get hasAboveLineText() {
    return !!this._aboveLineText;
  }

  get clef() {
    return this._segments.find((e) => e.clef)?.clef as Clef;
  }

  get isClef() {
    return this._segments.every((s) => {
      return s.isClef;
    });
  }

  get isBar() {
    return this._segments.every((s) => {
      return s.isBar;
    });
  }

  get isBarFirst() {
    return this._segments.length > 0 && this._segments[0].isBarFirst;
  }

  get top() {
    return min(this._segments.map((e) => e.top)) || 0;
  }

  get index() {
    return this._index;
  }

  public leadingSpace = 0;

  get keepWithNext() {
    return this._lyric?.keepWithNext || false;
  }

  get gregorioSyllable() {
    return this._gregorioSyllable;
  }

  get lyric() {
    return this._lyric;
  }

  public get hasLyric() {
    return !!this._lyric;
  }

  get hasNabc() {
    return this._segments.some((e) => e.hasNabc);
  }

  get hasEndOfLine() {
    if (this._segments.length === 0) {
      return false;
    }

    return this._segments[this._segments.length - 1].hasEndOfLine;
  }

  get endOfLineInfo() {
    return this._segments[this._segments.length - 1].endOfLineInfo;
  }

  get hasAnySegments() {
    return this._segments.length > 0;
  }

  get segments() {
    return this._segments;
  }

  _isLastSyllable = false;

  get isLastSyllable() {
    return this._isLastSyllable;
  }

  set isLastSyllable(value: boolean) {
    this._isLastSyllable = value;
  }

  constructor(
    context: ChantContext,
    gregorioSyllable: GregorioSyllable,
    index?: number,
  ) {
    super(context);
    this._gregorioSyllable = gregorioSyllable;
    this._index = index;
  }

  addSegments(segments: ChantSegmentElement[]) {
    this._segments = this._segments.concat(segments);
  }

  addLyric(lyric: Lyric) {
    this._lyric = lyric;
  }

  addAboveLineText(aboveLineText: AboveLineText) {
    this._aboveLineText = aboveLineText;
  }

  performLayout(notationBounds: Rect) {
    let bounds = Rect.create(0, notationBounds.y, 0, notationBounds.height);

    const interelementspace = this._distanceConverter.lengthToPx(this._context.interelementspace);
    let minX = 0;
    for (let i = 0; i < this._segments.length; i++) {
      const segment = this._segments[i];
      segment.performLayout(bounds, !this.hasAboveLineText);
      segment.bounds.setX(bounds.right + (i > 0 ? interelementspace : segment.bounds.x));
      minX = Math.min(minX, segment.bounds.x);
      bounds = bounds.horizontalUnion(segment.bounds);
    }

    bounds.setWidth(bounds.width + minX);
    bounds.setX(0);
    this.bounds = bounds;

    if (this._lyric !== null) {
      this._lyric.performLayout();
      const spacelinestext = this._distanceConverter.lengthToPx(this._context.spacelinestext);
      this._lyric.bounds.setY(notationBounds.bottom + spacelinestext);
    }

    if (this._aboveLineText !== null) {
      this._aboveLineText.performLayout();
      const spacelinestext = this._distanceConverter.lengthToPx(this._context.abovelinestextraise);
      this._aboveLineText.bounds.setY(spacelinestext);
      // bounds = bounds.horizontalUnion(this._aboveLineText.bounds);
    }
  }

  createRect(): SVGElement {
    const y = -this._context.interactiveElementsYPadding;
    const height = this.bounds.height + this._context.interactiveElementsYPadding * 2;
    const x = this._lyric?.bounds.x || 0;
    const width = this.bounds.width;

    const rootElement = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    rootElement.setAttribute('transform', 'translate(' + this.bounds.x + ',' + 0 + ')');
    rootElement.classList.add('syllable');
    if (this._index) {
      rootElement.classList.add('syllable-' + String(this._index));
    }

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
    rect.setAttribute('x', String(x));
    rect.setAttribute('y', String(y));
    rect.setAttribute('width', String(width));
    rect.setAttribute('height', String(height));
    rect.setAttribute('fill', '#ffffff');
    rect.setAttribute('fill-opacity', '0');

    rect.classList.add('bg-rect');
    rootElement.appendChild(rect);

    return rootElement;
  }

  createSvgFragments(): SVGElement[] {
    const rootElement = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    rootElement.classList.add('syllable');

    if (this._index) {
      rootElement.classList.add('syllable-' + String(this._index));
    }

    rootElement.setAttribute('transform', 'translate(' + this.bounds.x + ',' + 0 + ')');

    for (const segment of this._segments) {
      segment.isLastSyllable = this.isLastSyllable;
      for (const fragment of segment.createSvgFragments()) {
        rootElement.appendChild(fragment);
      }
    }

    if (this._lyric !== null) {
      for (const fragment of this._lyric.createSvgFragments()) {
        rootElement.appendChild(fragment);
      }
    }

    if (this._aboveLineText !== null) {
      for (const fragment of this._aboveLineText.createSvgFragments()) {
        rootElement.appendChild(fragment);
      }
    }

    return [
      rootElement,
      ...super.createSvgFragments(),
    ];
  }

  split(
    segmentIndex: number, visualizerIndex: number):
    [ChantSyllableElement | undefined, ChantSyllableElement | undefined] {
    let firstSyllable: ChantSyllableElement | undefined, secondSyllable: ChantSyllableElement | undefined = undefined;

    const targetSegment = this._segments[segmentIndex];
    const [segment1, segment2] = targetSegment.split(visualizerIndex);
    const firstSylSegments = this._segments.slice(0, segmentIndex);
    const secondSylSegments = this._segments.slice(segmentIndex + 1);

    if (firstSylSegments.length > 0 || segment1) {
      firstSyllable = new ChantSyllableElement(this._context, this._gregorioSyllable, this._index);

      if (this._lyric !== null) {
        firstSyllable.addLyric(this._lyric);
      }

      if (segment1) {
        firstSyllable.addSegments([...firstSylSegments, segment1]);
      } else {
        firstSyllable.addSegments(firstSylSegments);
      }

      if (targetSegment.hasHepisema) {
        const lastSegment = firstSyllable.segments[firstSyllable.segments.length - 1];
        lastSegment.addVisualizer(new HorizontalEpisema(this._context, lastSegment.segment));
      }
    }

    if (secondSylSegments.length > 0 || segment2) {
      secondSyllable = new ChantSyllableElement(this._context, this._gregorioSyllable, this._index);

      if (this._lyric !== null && segment1 === undefined) {
        secondSyllable.addLyric(this._lyric);
      }

      if (segment2) {
        secondSyllable.addSegments([segment2, ...secondSylSegments]);
      } else {
        secondSyllable.addSegments(secondSylSegments);
      }

      if (targetSegment.hasHepisema) {
        const firstSegment = secondSyllable.segments[0];
        firstSegment.popVisualizer();
        firstSegment.addVisualizer(new HorizontalEpisema(this._context, firstSegment.segment));
      }
    }

    return [firstSyllable, secondSyllable];
  }

  getBreakpoints(): SyllableBreakpoint[] {
    const breakpoints: SyllableBreakpoint[] = [];

    for (let i = 0; i < this._segments.length; i++) {
      const segment = this._segments[i];
      const segmentBreakpoints = segment.getBreakpoints(i).map((bp) => ({
        ...bp,
        right: bp.right + segment.bounds.x,
      }));

      breakpoints.push(...segmentBreakpoints);
    }

    return breakpoints;
  }
}
